import {
  paginationMetadataSchema,
  paginationSchema,
} from '@snap-types/models/pagination';
import { userWithSettingsSchema } from '@snap-types/models/user';
import { z } from 'zod/v4';

export const ADMIN_USER_SORT_FIELDS = ['displayName', 'email'] as const;

export const adminUserSortFieldSet = new Set(ADMIN_USER_SORT_FIELDS);

export type AdminUserSortField = (typeof ADMIN_USER_SORT_FIELDS)[number];

export type AdminUserSortQuery = `${AdminUserSortField}:${'asc' | 'desc'}`;

const adminUserSortQueryValidator = z.string().check((ctx) => {
  const [field, direction] = ctx.value.split(':');

  if (!adminUserSortFieldSet.has(field as AdminUserSortField)) {
    ctx.issues.push({
      code: 'custom',
      message: `invalid sort field, must be one of: ${ADMIN_USER_SORT_FIELDS.join(
        ', ',
      )}`,
      input: ctx.value,
    });
  }

  if (direction !== 'asc' && direction !== 'desc') {
    ctx.issues.push({
      code: 'custom',
      message: 'invalid sort direction, must be one of: asc, desc',
      input: ctx.value,
    });
  }
});

export const searchAdminUsersInputValidator = z.object({
  search: z.string().optional(),
  sort: z.array(adminUserSortQueryValidator).default([]),
  ...paginationSchema.shape,
});

export type SearchAdminUsersInput = z.input<
  typeof searchAdminUsersInputValidator
>;

export const searchAdminUsersQueryStringValidator =
  searchAdminUsersInputValidator.pick({
    search: true,
    sort: true,
    page: true,
    perPage: true,
  });

export type SearchAdminUsersQueryString = z.output<
  typeof searchAdminUsersQueryStringValidator
>;

export const searchAdminUsersSerializer = z.object({
  data: z.array(userWithSettingsSchema),
  pagination: paginationMetadataSchema,
});

export type SearchAdminUsersResponse = z.output<
  typeof searchAdminUsersSerializer
>;
